import { Link } from "react-router-dom";
import { emergencyHelplines, emergencyServices } from "@/mocks/siteContent";
import { buildEmergencyDirectoryHref } from "@/utils/emergency";
import useReveal from "@/hooks/useReveal";

export default function EmergencyPanel() {
  const servicesReveal = useReveal<HTMLDivElement>(0.1);
  const helplinesReveal = useReveal<HTMLDivElement>(0.1);

  return (
    <aside className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50/95 p-4 backdrop-blur md:p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-red-600 text-white">
          <i className="ri-alarm-warning-line text-lg" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-sm font-bold leading-tight text-foreground-950">
            Emergency &amp; Essential
          </h2>
          <p className="mt-0.5 text-[11px] leading-snug text-foreground-500">
            Time-critical services, one tap away
          </p>
        </div>
        <span className="flex-none rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-bold text-red-600">
          24&times;7
        </span>
      </div>

      <div ref={servicesReveal.ref} className="mt-4 grid grid-cols-2 gap-2">
        {emergencyServices.map((item, index) => (
          <Link
            key={item.id}
            to={buildEmergencyDirectoryHref(item)}
            title={`${item.label} — ${item.description}`}
            className={`group flex cursor-pointer items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-2 transition-colors hover:border-red-300 hover:bg-red-50 ${
              servicesReveal.visible ? "animate-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: `${index * 45}ms` }}
          >
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded border border-red-100 bg-red-50 text-red-600 transition-colors group-hover:bg-red-100">
              <i className={`${item.icon} text-sm`} />
            </span>
            <span className="truncate text-xs font-semibold text-foreground-800">{item.label}</span>
          </Link>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2 border-t border-background-200 pt-4">
        <span className="flex h-4 w-4 items-center justify-center text-red-600">
          <i className="ri-phone-fill text-xs" />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-red-600">
          Emergency helplines
        </p>
      </div>

      <div ref={helplinesReveal.ref} className="mt-2.5 grid grid-cols-2 gap-2">
        {emergencyHelplines.map((line, index) => (
          <a
            key={line.id}
            href={`tel:${line.number}`}
            title={`${line.label} — ${line.note}`}
            className={`group flex cursor-pointer items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 transition-colors hover:bg-red-100 ${
              helplinesReveal.visible ? "animate-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: `${index * 70}ms` }}
          >
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded bg-red-600 text-white">
              <i className={`${line.icon} text-sm`} />
            </span>
            <span className="min-w-0">
              <span className="block font-heading text-sm font-extrabold leading-none text-red-700">
                {line.number}
              </span>
              <span className="mt-0.5 block truncate text-[10px] leading-none text-red-500">
                {line.label}
              </span>
            </span>
          </a>
        ))}
      </div>

      <Link
        to="/emergency-services"
        className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-phone-line text-sm" />
        </span>
        View all emergency services
      </Link>
    </aside>
  );
}